import React from 'react'
import { deletePodcast, usePodcasts, apiRequest, apiRequestForm, createFromYouTubeAndStartScript } from '../api'
import Button from '../components/ui/button'
import { toUtcDate } from '../lib/date-utils'
import PodcastCard from '../components/podcast-card'
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom'
import type { StyleType } from '../types/podcast-generation'
import { useAuth } from '../hooks/use-auth'
import { PodcastInput } from '../components/podcast-input'
import { track } from '../lib/analytics/posthog'
import { clearNewPodcastDraft } from '../lib/new-podcast-storage'

const Dashboard: React.FC = () => {
  const { user } = useAuth()
  const { data: podcasts = [], isLoading: isLoadingPodcasts, refetch } = usePodcasts()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const location = useLocation()

  const [isGenerating, setIsGenerating] = React.useState(false)
  const [generateError, setGenerateError] = React.useState<string | null>(null)

  // Initial values for the PodcastInput (used when user comes from marketing signup)
  const [inputInitialValues, setInputInitialValues] = React.useState<{
    prompt?: string
    url?: string
    style?: StyleType
    durationMinutes?: number
  } | null>(null)

  // Handle dashboard intent from query params (from Chrome extension)
  React.useEffect(() => {
    const intent = searchParams.get('intent')
    const url = searchParams.get('url')
    const styleParam = (searchParams.get('style') || '').toLowerCase()
    const durationParam = Number(searchParams.get('durationMinutes') || '')

    if (intent === 'create-from-url' && url) {
      // Sanitize style and duration
      const style: StyleType = (['educational', 'conversational', 'narrative', 'deep dive'] as const).includes(styleParam as any)
        ? (styleParam as StyleType)
        : 'educational'
      const durationMinutes = Number.isFinite(durationParam) && durationParam > 0 ? Math.min(Math.max(Math.floor(durationParam), 1), 60) : 5

      // Pre-fill PodcastInput with URL from Chrome extension
      setInputInitialValues({
        url,
        style,
        durationMinutes,
      })

      // Clear intent params to avoid re-triggering on refresh / navigation
      if (location.search) {
        navigate(location.pathname, { replace: true })
      }
    }
    // run when params change
  }, [searchParams, location.pathname, location.search, navigate])

  React.useEffect(() => {
    if (!user) return
    if (user.onboardingCompleted) return

    // If user has signupData from marketing signup, pre-fill the input instead of opening dialog
    const sdRaw = user.signupData as Record<string, unknown> | null
    if (sdRaw) {
      // Normalize casing (backend may return PascalCase via System.Text.Json)
      const sd = {
        websiteUrl: (sdRaw.websiteUrl as string) ?? (sdRaw.WebsiteUrl as string),
        youtubeUrl: (sdRaw.youtubeUrl as string) ?? (sdRaw.YoutubeUrl as string),
        text: (sdRaw.text as string) ?? (sdRaw.Text as string),
        fileUrl: (sdRaw.fileUrl as string) ?? (sdRaw.FileUrl as string),
        durationMinutes: (sdRaw.durationMinutes as number) ?? (sdRaw.DurationMinutes as number),
        style: (sdRaw.style as string) ?? (sdRaw.Style as string),
        contentType: (sdRaw.contentType as string) ?? (sdRaw.ContentType as string),
      }

      // Build initial values for PodcastInput
      let prompt: string | undefined
      let url: string | undefined
      const style = (sd.style as StyleType) || 'educational'
      const durationMinutes = sd.durationMinutes || 5

      // Map contentType to prompt/url
      if (sd.contentType === 'website' && sd.websiteUrl) {
        url = sd.websiteUrl
      } else if (sd.contentType === 'youtube' && sd.youtubeUrl) {
        url = sd.youtubeUrl
      } else if ((sd.contentType === 'text' || sd.contentType === 'notes') && sd.text) {
        prompt = sd.text
      } else if (sd.contentType === 'document') {
        // Document was uploaded but file can't be passed, fallback to prompt if text exists
        prompt = sd.text
      } else {
        // Fallback - try any text or URL we have
        if (sd.websiteUrl) {
          url = sd.websiteUrl
        } else if (sd.text) {
          prompt = sd.text
        }
      }

      if (prompt || url) {
        setInputInitialValues({ prompt, url, style, durationMinutes })
      }

      // Mark onboarding complete (fire and forget)
      import('../api/auth').then(m => { m.completeOnboarding().catch(() => { }) })
    }
    // run once after user loads
  }, [user])

  // Compute recents client-side per request
  const recents = React.useMemo(() => {
    if (!podcasts?.length) return [] as typeof podcasts
    return [...podcasts]
      .sort((a, b) => toUtcDate(b.updatedAt).getTime() - toUtcDate(a.updatedAt).getTime())
      .slice(0, 3)
  }, [podcasts])

  // Actual episode templates with durations
  const templates = [
    {
      id: 'stoicism',
      title: 'Exploring Ancient Roman Stoicism',
      minutes: 5,
      style: 'narrative' as StyleType,
      desc: 'A concise dive into Stoic principles and their modern relevance.',
      prompt:
        'A short episode on Ancient Roman Stoicism. Touch on a few core principles, a quick story, and why it still matters today.'
    },
    {
      id: 'ml-beginners',
      title: "Understanding Machine Learning: A Beginner's Guide",
      minutes: 10,
      style: 'educational' as StyleType,
      desc: 'A friendly primer on core ML concepts and terminology.',
      prompt:
        'Explain machine learning for beginners with simple examples and plain language.'
    },
    {
      id: 'remote-work-2025',
      title: 'The Future of Remote Work in 2025',
      minutes: 20,
      style: 'conversational' as StyleType,
      desc: 'Trends shaping hybrid work, from tooling to policy.',
      prompt:
        'Discuss the future of remote work in 2025: emerging trends, challenges, and a practical tip for teams.'
    },
  ] as const

  // Single-action: pre-fill PodcastInput with chosen template
  const handleStartWithTemplate = (templateId: string) => {
    const t = templates.find(tt => tt.id === templateId)
    if (!t) return
    setInputInitialValues({
      prompt: t.prompt,
      style: t.style,
      durationMinutes: t.minutes,
    })
    // Scroll to input for better UX
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const handleInputGenerate = async (data: {
    prompt: string
    url?: string
    file?: File
    style: StyleType
    durationMinutes: number
    languageIso: string
    isPublic: boolean
  }) => {
    setIsGenerating(true)
    const { prompt, url, file, style, durationMinutes, languageIso, isPublic } = data

    // Helper to detect YouTube URLs
    const isYouTubeUrl = (u: string) => /youtube\.com\/watch|youtu\.be\/|youtube\.com\/embed/.test(u)

    try {
      let podcastId = ''

      if (file) {
        const form = new FormData()
        form.append('file', file)
        form.append('style', style)
        form.append('durationMinutes', String(durationMinutes))
        form.append('languageIso', languageIso)
        if (prompt.trim()) {
          form.append('additionalInstructions', prompt.trim())
        }
        form.append('isPublic', String(isPublic))

        const res = await apiRequestForm<{ id: string }>('/api/podcast/from-source/document', form)
        podcastId = res.id
        track('podcast_script_generation_started', {
          source: 'document',
          style,
          duration_minutes: durationMinutes,
          podcast_id: podcastId
        })
      } else if (url && isYouTubeUrl(url)) {
        const res = await createFromYouTubeAndStartScript({
          youtubeUrl: url,
          style,
          durationMinutes,
          languageIso,
          additionalInstructions: prompt.trim() || undefined,
          isPublic: false
        })
        podcastId = res.id
        track('podcast_script_generation_started', {
          source: 'youtube',
          style,
          duration_minutes: durationMinutes,
          podcast_id: podcastId
        })
      } else if (url) {
        const body: any = {
          url,
          style,
          durationMinutes,
          languageIso,
          isPublic: false
        }
        if (prompt.trim()) {
          body.additionalInstructions = prompt.trim()
        }
        const res = await apiRequest<{ id: string }>('/api/podcast/from-source/url', {
          method: 'POST',
          body: JSON.stringify(body)
        })
        podcastId = res.id
        track('podcast_script_generation_started', {
          source: 'url',
          style,
          duration_minutes: durationMinutes,
          podcast_id: podcastId
        })
      } else {
        // Prompt only
        const res = await apiRequest<{ id: string }>('/api/podcast/from-source/prompt', {
          method: 'POST',
          body: JSON.stringify({
            prompt: prompt.trim(),
            style,
            durationMinutes,
            languageIso,
            isPublic: false
          })
        })
        podcastId = res.id
        track('podcast_script_generation_started', {
          source: 'prompt',
          style,
          duration_minutes: durationMinutes,
          podcast_id: podcastId
        })
      }

      // Success
      await refetch()
      clearNewPodcastDraft() // Just in case
      navigate(`/podcasts/${podcastId}?showLoader=true`)

    } catch (err: unknown) {
      console.error(err)
      const error = err as { message?: string }
      const errorMessage = error?.message || 'Failed to create podcast'
      setGenerateError(errorMessage)
    } finally {
      setIsGenerating(false)
    }
  }

  const hasPodcasts = podcasts?.length > 0
  const recs = user?.recommendations ?? []
  const hasMultipleRecs = (recs?.length ?? 0) > 0

  const handleStartWithRecommendation = (idx: number) => {
    const r = recs[idx]
    if (!r) return
    setInputInitialValues({
      prompt: r.newPrompt,
    })
    // Scroll to input for better UX
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  return (
    <div className="space-y-12 pb-20">

      {/* Search / Hero Input Section */}
      <div className="py-8 sm:py-12 bg-transparent text-center">
        <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-foreground mb-6">
          Turn your ideas into podcasts
        </h1>
        <p className="text-lg text-foreground/60 max-w-2xl mx-auto mb-10 leading-relaxed">
          Transform any idea, document, or website into an engaging audio conversation.
        </p>

        <PodcastInput 
          onGenerate={handleInputGenerate} 
          isGenerating={isGenerating} 
          initialValues={inputInitialValues}
          onInitialValuesApplied={() => setInputInitialValues(null)}
          error={generateError}
          onErrorClear={() => setGenerateError(null)}
        />
      </div>


      {/* Recents - show only if there are recents */}
      {isLoadingPodcasts ? (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4" aria-busy="true">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="h-28 rounded-xl border border-black/5 bg-white animate-pulse" />
          ))}
        </div>
      ) : recents.length > 0 ? (
        <section className="space-y-4 max-w-5xl mx-auto" role="region" aria-labelledby="recents-heading">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <h2 id="recents-heading" className="text-xl font-semibold text-foreground">Continue where you left off</h2>
            <Button variant="ghost" size="sm" onClick={() => navigate('/podcasts')}>View all</Button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {recents.map((p) => (
              <PodcastCard
                key={p.id}
                podcast={p}
                onRefetch={async () => { await refetch() }}
                onDelete={async (id: string) => {
                  await deletePodcast(id);
                  await refetch()
                }}
              />
            ))}
          </div>
        </section>
      ) : null}

      {/* Recommendations or Templates - Keep these but maybe style them consistently */}
      {!hasPodcasts && (
        <div className="max-w-5xl mx-auto">
          {hasMultipleRecs ? (
            <section className="space-y-4" role="region" aria-labelledby="recs-heading">
              <h2 id="recs-heading" className="text-xl font-semibold text-foreground">Start from your recommendations</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                {recs.map((r, i) => (
                  <button
                    key={`${r.title}-${i}`}
                    type="button"
                    aria-label={`Use recommendation: ${r.title}`}
                    onClick={() => handleStartWithRecommendation(i)}
                    className="w-full cursor-pointer text-left rounded-xl border border-black/5 bg-white p-5 transition-all hover:shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="truncate font-medium text-foreground" title={r.title}>{r.title}</p>
                        <p className="text-sm text-foreground/60 mt-1 whitespace-pre-line">{r.description}</p>
                      </div>
                    </div>
                    <p className="text-sm text-brand-700 mt-3">Use recommendation →</p>
                  </button>
                ))}
              </div>
            </section>
          ) : (
            <section className="space-y-4" role="region" aria-labelledby="templates-heading">
              <h2 id="templates-heading" className="text-xl font-semibold text-foreground">Start with a template</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                {templates.map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    aria-label={`Use template: ${t.title}`}
                    onClick={() => handleStartWithTemplate(t.id)}
                    className="w-full cursor-pointer text-left rounded-xl border border-black/5 bg-white p-5 transition-all hover:shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="truncate font-medium text-foreground" title={t.title}>{t.title}</p>
                        <p className="text-sm text-foreground/60 mt-1">{t.desc}</p>
                      </div>
                    </div>
                    <p className="text-sm text-brand-700 mt-3">Use template →</p>
                  </button>
                ))}
              </div>
            </section>
          )}
        </div>
      )}

    </div>
  )
}

export default Dashboard
